import React, { ReactNode } from "react";
import styled from "styled-components";
import { Box, BoxProps } from "../box/Box";
import IndexIcon from "../shared/IndexIcon";
import useRovingIndex from "../../hooks/useRovingIndex";

interface Props extends BoxProps {
    children: ReactNode | ReactNode[];
    overlayImage?: ReactNode;
    showSlideIndex?: boolean;
    slideIndexPosition?: "left" | "right";
}

const Container = styled(Box)`
    position: relative;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr min-content 1fr;
`;

const Showcase = styled(Box)`
    grid-row: 2 / span 1;
    grid-column: 1 / -1;
    overflow: hidden;
`;

const MediaList = styled(Box)`
    position: relative;
    display: grid;
    grid-auto-columns: 100%;
    /* grid-gap: 16px; */
    transition: transform 400ms cubic-bezier(0, 0, 0, 1);
    backface-visibility: hidden;
    z-index: 1;
`;

const MediaItem = styled(Box)`
    grid-row: 1 / span 1;

    img,
    video {
        width: 100%;
        display: block;
    }
`;

const OverlayImage = styled.div`
    grid-column: 2 / span 1;
    grid-row: 1 / -1;
    z-index: 2;
`;

export default function MediaShowcase01(
    {
        overlayImage,
        children,
        showSlideIndex = true,
        slideIndexPosition = "right",
        ...rest
    }: Props) {
    const total = React.Children.count(children);
    const [currentIndex, updateIndex] = useRovingIndex(total - 1);
    const showDots = showSlideIndex && total > 1;

    return (
        <Container {...rest}>
            {overlayImage && <OverlayImage>{overlayImage}</OverlayImage>}
            <Showcase>
                <MediaList
                    style={{ transform: `translateX(${currentIndex * -100}%)` }}
                >
                    {React.Children.map(children, (child, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <MediaItem key={index} gridColumn={index + 1}>
                            {child}
                        </MediaItem>
                    ))}
                </MediaList>
            </Showcase>
            {showDots && (
                <Box
                    display={["none", "flex"]}
                    gridRow="3"
                    gridColumn={slideIndexPosition === "right" ? -2 : 1}
                    justifyContent={
                        slideIndexPosition === "right"
                            ? "flex-end"
                            : "flex-start"
                    }
                    alignItems="flex-start"
                    py={4}
                >
                    {React.Children.map(children, (child, index) => (
                        <IndexIcon
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            active={index === currentIndex}
                            onClick={() => updateIndex(index)}
                            style={{ cursor: "pointer" }}
                        />
                    ))}
                </Box>
            )}
        </Container>
    );
}
